

interface CryptoMatch {
  date: string;
  similarityScore: number;
  window: number[];
  outcome: number;
}

interface CryptoPatternData {
  coin: string;
  symbol: string;
  currentPrice: number;
  currentWindow: number[];
  currentDates: string[];
  matches: CryptoMatch[];
  bullishCount: number;
  avgOutcome: number;
  lookbackDays: number;
  forwardDays: number;
}

interface Props {
  data: CryptoPatternData;
}

// Build cumulative return series from pct_change array, starting at 0
function toCumulative(pcts: number[]): number[] {
  const out: number[] = [0];
  for (const p of pcts) out.push(out[out.length - 1] + p);
  return out;
}

// Convert a series of values to SVG polyline points within a viewport
function toPoints(values: number[], xStart: number, xEnd: number, yMin: number, yMax: number, svgH: number): string {
  const n = values.length;
  const yRange = yMax - yMin || 1;
  return values.map((v, i) => {
    const x = xStart + (i / (n - 1)) * (xEnd - xStart);
    const y = svgH - 4 - ((v - yMin) / yRange) * (svgH - 8);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
}

const CryptoPatternChart: React.FC<Props> = ({ data }) => {
  const { symbol, currentPrice, currentWindow, currentDates, matches, bullishCount, avgOutcome, lookbackDays, forwardDays } = data;
  if (!matches.length) return null;

  const best = matches[0];
  const W = 320;
  const H = 70;
  const midX = W * 0.55; // divider between lookback and "what happened next"
  const pad = 12;

  // Current cumulative return
  const currCum = toCumulative(currentWindow);

  // Best match: window + simulated outcome
  // Extend the match window with forwardDays worth of estimated movement
  // We represent the outcome as a single projected line from end of window
  const matchCum = toCumulative(best.window);

  // Combined range for Y scale
  const allVals = [...currCum, ...matchCum];
  const yMin = Math.min(...allVals) - 0.5;
  const yMax = Math.max(...allVals) + 0.5;

  const currPoints = toPoints(currCum, pad, midX - 6, yMin, yMax, H);
  const matchPoints = toPoints(matchCum, pad, midX - 6, yMin, yMax, H);

  // Outcome arrow: from end of match line to projected endpoint
  const matchEndY = H - 4 - ((matchCum[matchCum.length - 1] - yMin) / (yMax - yMin || 1)) * (H - 8);
  const outcomeEndVal = matchCum[matchCum.length - 1] + best.outcome;
  const outcomeEndYClamped = H - 4 - (((outcomeEndVal) - yMin) / (yMax - yMin || 1)) * (H - 8);
  const outcomeEndYDisplay = Math.max(4, Math.min(H - 4, outcomeEndYClamped));

  const isBullish = best.outcome > 0;
  const outcomeColor = isBullish ? '#22c55e' : '#ef4444';

  // Start date label (first date in current window)
  const startDate = currentDates[0] ? currentDates[0].slice(5) : ''; // MM-DD
  const endDate = currentDates[currentDates.length - 1] ? currentDates[currentDates.length - 1].slice(5) : '';

  return (
    <div style={{ marginTop: 10, background: '#0f172a', borderRadius: 8, padding: '10px 12px', border: '1px solid #1e293b' }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: 1 }}>
            {symbol} PATTERN MATCH
          </span>
          <span style={{ fontSize: 11, color: '#64748b' }}>
            ${currentPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
            background: '#1e3a5f', color: '#60a5fa'
          }}>
            {bullishCount}/{matches.length} bullish
          </span>
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
            background: avgOutcome > 0 ? '#14532d' : '#450a0a',
            color: avgOutcome > 0 ? '#4ade80' : '#f87171'
          }}>
            avg {avgOutcome > 0 ? '+' : ''}{avgOutcome.toFixed(1)}%
          </span>
        </div>
      </div>

      {/* Chart */}
      <svg width={W} height={H} style={{ display: 'block', overflow: 'visible' }}>
        {/* Zero line */}
        <line
          x1={pad} y1={H - 4 - ((-yMin) / (yMax - yMin || 1)) * (H - 8)}
          x2={midX - 6} y2={H - 4 - ((-yMin) / (yMax - yMin || 1)) * (H - 8)}
          stroke="#1e293b" strokeWidth={1} strokeDasharray="2,2"
        />

        {/* Divider: "today" */}
        <line x1={midX} y1={4} x2={midX} y2={H - 4} stroke="#334155" strokeWidth={1} strokeDasharray="3,2" />
        <text x={midX + 3} y={10} fill="#475569" fontSize={8}>→ next {forwardDays}d</text>

        {/* Best match line (historical) — amber, slightly transparent */}
        <polyline
          points={matchPoints}
          fill="none"
          stroke="#f59e0b"
          strokeWidth={1.5}
          strokeOpacity={0.6}
          strokeDasharray="4,2"
        />

        {/* Current pattern line — blue, solid */}
        <polyline
          points={currPoints}
          fill="none"
          stroke="#60a5fa"
          strokeWidth={2}
        />

        {/* Outcome arrow from end of match */}
        <line
          x1={midX} y1={matchEndY}
          x2={W - pad} y2={outcomeEndYDisplay}
          stroke={outcomeColor}
          strokeWidth={2}
          strokeOpacity={0.8}
        />
        {/* Arrowhead */}
        <circle cx={W - pad} cy={outcomeEndYDisplay} r={3} fill={outcomeColor} />

        {/* Dot at current end */}
        {(() => {
          const lastCurrY = currCum[currCum.length - 1];
          const y = H - 4 - ((lastCurrY - yMin) / (yMax - yMin || 1)) * (H - 8);
          return <circle cx={midX - 6} cy={y} r={3} fill="#60a5fa" />;
        })()}

        {/* Date labels */}
        <text x={pad} y={H + 12} fill="#475569" fontSize={8}>{startDate}</text>
        <text x={midX - 28} y={H + 12} fill="#475569" fontSize={8}>{endDate}</text>
      </svg>

      {/* Match rows */}
      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {matches.map((m, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
            <span style={{
              minWidth: 32, textAlign: 'center', fontWeight: 700,
              padding: '1px 5px', borderRadius: 4,
              background: i === 0 ? '#451a03' : '#1c1917',
              color: i === 0 ? '#fbbf24' : '#78716c',
              border: i === 0 ? '1px solid #92400e' : '1px solid #292524'
            }}>
              {i === 0 ? '★' : `#${i + 1}`}
            </span>
            <span style={{ color: '#94a3b8', minWidth: 55 }}>{m.date.slice(0, 7)}</span>
            <div style={{
              flex: 1, height: 4, borderRadius: 2, background: '#1e293b', overflow: 'hidden'
            }}>
              <div style={{
                height: '100%', width: `${m.similarityScore}%`,
                background: i === 0 ? '#f59e0b' : '#475569', borderRadius: 2
              }} />
            </div>
            <span style={{ color: '#64748b', minWidth: 38, textAlign: 'right' }}>
              {m.similarityScore}%
            </span>
            <span style={{
              minWidth: 55, textAlign: 'right', fontWeight: 600,
              color: m.outcome > 0 ? '#4ade80' : '#f87171'
            }}>
              {m.outcome > 0 ? '+' : ''}{m.outcome.toFixed(1)}%
            </span>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 10, color: '#475569' }}>
        <span><span style={{ color: '#60a5fa' }}>—</span> Current {lookbackDays}d pattern</span>
        <span><span style={{ color: '#f59e0b' }}>- -</span> Best match (historical)</span>
        <span><span style={{ color: '#94a3b8' }}>→</span> Historical outcome</span>
      </div>
    </div>
  );
};

export default CryptoPatternChart;
